// Story 1.22 — post-approval creator profile edit form.
//
// Structurally similar to Story 1.21's CreatorApplicationForm. Reuses
// the same field components (LinkedAccountsField, CertPreview) but
// swaps the image-upload hook (profile prefix) and adds the bio field.
// The live cert preview reinforces the snapshot rule above it.

import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

import {
  Button,
  Card,
  CardContent,
  Input,
  Label,
  Switch,
  Textarea,
} from "@greenhold/components";

import { CreatorApplicationCertPreview } from "../creator-application/CreatorApplicationCertPreview";
import { LinkedAccountsField } from "../creator-application/LinkedAccountsField";
import type { LinkedAccountInput } from "../creator-application/useSubmitCreatorApplication";
import { ProfileImageDropzone } from "./ProfileImageDropzone";
import type { CreatorProfile } from "./useCreatorProfile";
import {
  type UpdateCreatorProfileError,
  type UpdateCreatorProfileRequest,
  useUpdateCreatorProfile,
} from "./useUpdateCreatorProfile";

const REGIONS = [
  { value: "GB", label: "United Kingdom" },
  { value: "GB-ENG", label: "England" },
  { value: "GB-WLS", label: "Wales" },
  { value: "GB-SCT", label: "Scotland" },
  { value: "GB-NIR", label: "Northern Ireland" },
  { value: "US", label: "United States" },
  { value: "DE", label: "Germany" },
  { value: "FR", label: "France" },
  { value: "ES", label: "Spain" },
  { value: "PT", label: "Portugal" },
  { value: "IT", label: "Italy" },
  { value: "NL", label: "Netherlands" },
  { value: "BE", label: "Belgium" },
  { value: "SE", label: "Sweden" },
  { value: "NO", label: "Norway" },
  { value: "DK", label: "Denmark" },
  { value: "FI", label: "Finland" },
  { value: "PL", label: "Poland" },
  { value: "IE", label: "Ireland" },
  { value: "AU", label: "Australia" },
  { value: "CA", label: "Canada" },
];

const LANGUAGES = [
  { value: "en-GB", label: "English (UK)" },
  { value: "en-US", label: "English (US)" },
  { value: "de-DE", label: "German" },
  { value: "fr-FR", label: "French" },
  { value: "es-ES", label: "Spanish" },
  { value: "pt-PT", label: "Portuguese" },
  { value: "it-IT", label: "Italian" },
  { value: "nl-NL", label: "Dutch" },
  { value: "sv-SE", label: "Swedish" },
  { value: "no-NO", label: "Norwegian" },
  { value: "da-DK", label: "Danish" },
];

// 38, not the server's 40 hard cap: longest name that still renders on
// ONE line on the PatronageCertificate artifact. Keep in sync with
// CreatorApplicationForm.
const DISPLAY_NAME_MAX = 38;
const HELLO_TEXT_MAX = 140;
const BIO_MAX = 500;
const COUNTER_AMBER_THRESHOLD = 0.9;

interface Props {
  profile: CreatorProfile;
}

export function CreatorProfileForm({ profile }: Props) {
  const updateMutation = useUpdateCreatorProfile();

  const [displayName, setDisplayName] = useState(profile.display_name ?? "");
  const [helloText, setHelloText] = useState(profile.hello_text ?? "");
  const [bio, setBio] = useState(profile.bio ?? "");
  const [imageBlobPath, setImageBlobPath] = useState<string | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(
    profile.image_signed_url,
  );
  const initialLinkedAccounts: LinkedAccountInput[] = useMemo(() => {
    if (!profile.linked_accounts || profile.linked_accounts.length === 0) {
      return [{ kind: "twitch", url: "" }];
    }
    return profile.linked_accounts.map((la) => ({ kind: la.kind, url: la.url }));
  }, [profile.linked_accounts]);
  const [linkedAccounts, setLinkedAccounts] = useState<LinkedAccountInput[]>(
    initialLinkedAccounts,
  );
  const [region, setRegion] = useState(profile.region ?? "GB");
  const [language, setLanguage] = useState(profile.language ?? "en-GB");
  const [autoAssignOptOut, setAutoAssignOptOut] = useState(
    profile.portfolio_auto_assign_opt_out,
  );
  const [linkErrors, setLinkErrors] = useState<Record<number, string>>({});

  useEffect(() => {
    return () => {
      if (imagePreviewUrl && imagePreviewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  const isFormValid = useMemo(
    () =>
      displayName.trim().length > 0 &&
      displayName.trim().length <= DISPLAY_NAME_MAX &&
      helloText.trim().length > 0 &&
      helloText.trim().length <= HELLO_TEXT_MAX &&
      bio.trim().length <= BIO_MAX &&
      linkedAccounts.length > 0 &&
      linkedAccounts.every((la) => la.url.trim().startsWith("https://")),
    [displayName, helloText, bio, linkedAccounts],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLinkErrors({});
    if (!isFormValid) return;

    // Build a partial payload — only include fields that differ from
    // the current profile. This matches the server's diff semantics:
    // a no-op PATCH (all fields unchanged) returns 200 with no audit
    // row or email. The form computes the diff client-side too so the
    // network payload stays minimal.
    const patch: UpdateCreatorProfileRequest = {};
    if (displayName.trim() !== (profile.display_name ?? "")) {
      patch.display_name = displayName.trim();
    }
    if (helloText.trim() !== (profile.hello_text ?? "")) {
      patch.hello_text = helloText.trim();
    }
    const trimmedBio = bio.trim();
    if (trimmedBio !== (profile.bio ?? "")) {
      patch.bio = trimmedBio;
    }
    if (imageBlobPath) {
      patch.image_blob_path = imageBlobPath;
    }
    if (region !== (profile.region ?? "")) {
      patch.region = region;
    }
    if (language !== (profile.language ?? "")) {
      patch.language = language;
    }
    if (autoAssignOptOut !== profile.portfolio_auto_assign_opt_out) {
      patch.portfolio_auto_assign_opt_out = autoAssignOptOut;
    }
    const currentLinks = profile.linked_accounts ?? [];
    const linksDiffer =
      linkedAccounts.length !== currentLinks.length ||
      linkedAccounts.some(
        (la, i) =>
          la.kind !== currentLinks[i]?.kind || la.url !== currentLinks[i]?.url,
      );
    if (linksDiffer) {
      patch.linked_accounts = linkedAccounts.map((la) => ({
        kind: la.kind,
        url: la.url.trim(),
      }));
    }

    if (Object.keys(patch).length === 0) {
      toast("No changes to save");
      return;
    }

    try {
      await updateMutation.mutateAsync(patch);
      toast.success("Profile updated");
    } catch (err) {
      const detail = err as UpdateCreatorProfileError;
      if (detail.code === "LINKED_ACCOUNT_VALIDATION_FAILED" && detail.failures) {
        const errs: Record<number, string> = {};
        for (const f of detail.failures) {
          errs[f.index] = `Could not verify (${f.reason})`;
        }
        setLinkErrors(errs);
        toast.error(
          "Some linked accounts couldn't be verified — fix the highlighted rows.",
        );
      } else if (detail.code === "CREATOR_PROFILE_FORBIDDEN") {
        toast.error(detail.message ?? "Your creator status changed.");
        // The page-level query will re-fetch on next render and
        // surface the matching status card.
      } else {
        toast.error(detail.message ?? "Update failed. Try again.");
      }
    }
  };

  const displayCounterColor =
    displayName.length / DISPLAY_NAME_MAX >= COUNTER_AMBER_THRESHOLD
      ? "text-amber-700"
      : "opacity-60";
  const helloCounterColor =
    helloText.length / HELLO_TEXT_MAX >= COUNTER_AMBER_THRESHOLD
      ? "text-amber-700"
      : "opacity-60";
  const bioCounterColor =
    bio.length / BIO_MAX >= COUNTER_AMBER_THRESHOLD
      ? "text-amber-700"
      : "opacity-60";

  return (
    <form
      onSubmit={handleSubmit}
      className="grid grid-cols-1 lg:grid-cols-2 gap-8"
    >
      <div className="space-y-6">
        <div className="space-y-1">
          <Label htmlFor="display_name">Display name *</Label>
          <Input
            id="display_name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={DISPLAY_NAME_MAX}
            required
          />
          <p className={`text-xs text-right ${displayCounterColor}`}>
            {displayName.length}/{DISPLAY_NAME_MAX} characters
          </p>
        </div>

        <div className="space-y-1">
          <Label htmlFor="hello_text">Hello text *</Label>
          <Textarea
            id="hello_text"
            value={helloText}
            onChange={(e) => setHelloText(e.target.value)}
            maxLength={HELLO_TEXT_MAX}
            rows={3}
            required
          />
          <p className={`text-xs text-right ${helloCounterColor}`}>
            {helloText.length}/{HELLO_TEXT_MAX} characters
          </p>
        </div>

        <div className="space-y-1">
          <Label htmlFor="bio">Bio (optional, up to 500 characters)</Label>
          <Textarea
            id="bio"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            maxLength={BIO_MAX}
            rows={6}
            placeholder="A longer narrative about who you are and what you stream — appears on your public Greenhold profile."
          />
          <p className={`text-xs text-right ${bioCounterColor}`}>
            {bio.length}/{BIO_MAX} characters
          </p>
        </div>

        <ProfileImageDropzone
          blobPath={imageBlobPath}
          previewUrl={imagePreviewUrl}
          onUploaded={(blobPath, preview) => {
            setImageBlobPath(blobPath);
            setImagePreviewUrl(preview);
          }}
        />

        <LinkedAccountsField
          rows={linkedAccounts}
          errors={linkErrors}
          onChange={(next) => {
            setLinkedAccounts(next);
            setLinkErrors({});
          }}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="region">Region *</Label>
            <select
              id="region"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {REGIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="language">Primary language *</Label>
            <select
              id="language"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {LANGUAGES.map((l) => (
                <option key={l.value} value={l.value}>
                  {l.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-start justify-between gap-4 rounded-md border border-brand-forest-200 bg-brand-paper-50 p-4">
          <div className="space-y-1">
            <Label htmlFor="auto_assign_opt_out">
              Opt out of direct portfolio assignment
            </Label>
            <p className="text-xs opacity-70">
              When on, admins can't add you to a portfolio without you
              applying first. Applying to a recruiting portfolio still works
              — your application is your explicit consent. This doesn't
              affect your creator status or portfolios you're already on.
            </p>
          </div>
          <Switch
            id="auto_assign_opt_out"
            checked={autoAssignOptOut}
            onCheckedChange={setAutoAssignOptOut}
            aria-label="Opt out of direct portfolio assignment"
          />
        </div>

        <Button
          type="submit"
          variant="primary"
          disabled={!isFormValid || updateMutation.isPending}
        >
          {updateMutation.isPending ? "Saving…" : "Save profile"}
        </Button>
      </div>

      <div className="space-y-4">
        <Card className="bg-brand-paper-50 border-brand-forest-200">
          <CardContent className="p-4 text-sm">
            <strong>Snapshot rule reminder:</strong> This preview shows how NEW
            certificates of you will look from now on. Existing certificates
            issued before this update remain unchanged.
          </CardContent>
        </Card>
        {/* Profile editing doesn't touch the signature (the profile API
            doesn't expose it) — the cert preview just omits the ink. */}
        <CreatorApplicationCertPreview
          displayName={displayName}
          helloText={helloText}
          imagePreviewUrl={imagePreviewUrl}
          signaturePreviewUrl={null}
        />
      </div>
    </form>
  );
}
